import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/motion";
import { api, imgSrc } from "@/lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Seo
        title="ICROPSIA Farm Knowledge — Wheat & Maize Guides for Bihar"
        description="Practical guides on wheat cultivation, maize cultivation, seed selection and sowing practices for farmers in Bihar, from the ICROPSIA team."
      />
      <section className="pt-40 pb-16 bg-brand-ink relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-20" />
        <div className="container-site relative max-w-3xl">
          <Reveal>
            <Eyebrow light>Farm Knowledge</Eyebrow>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
              ICROPSIA <span className="font-editorial italic font-semibold text-brand-wheat">Farm Knowledge</span>
            </h1>
            <p className="mt-5 text-white/70 leading-relaxed">
              Practical, honest guides on wheat and maize cultivation, seed selection and sowing practices — written
              for farmers and agricultural partners in Bihar.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-site">
          {loading ? (
            <p className="text-brand-charcoal/50 text-sm">Loading articles…</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
              {posts.map((post, i) => (
                <Reveal key={post.slug} delay={(i % 3) * 0.08}>
                  <Link to={`/knowledge/${post.slug}`} data-testid={`blog-card-${post.slug}`} className="group block h-full">
                    <div className="rounded-3xl overflow-hidden border border-brand-deep/10 bg-white h-full flex flex-col hover:shadow-2xl hover:shadow-brand-deep/10 transition-shadow duration-500">
                      <div className="aspect-[16/10] overflow-hidden bg-brand-beige">
                        <img
                          src={imgSrc(post.cover_image)}
                          alt={post.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-6 flex flex-col flex-1">
                        <span className="text-[11px] font-bold tracking-wider uppercase text-brand-gold">{post.category}</span>
                        <h2 className="mt-2 font-display font-bold text-brand-deep text-lg leading-snug group-hover:text-brand-forest transition-colors">
                          {post.title}
                        </h2>
                        <p className="mt-2 text-sm text-brand-charcoal/60 leading-relaxed line-clamp-2 flex-1">{post.excerpt}</p>
                        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-brand-deep group-hover:gap-3 transition-all">
                          Read Article <ArrowUpRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
